import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useReactTable, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { usePanelProcessing } from './hooks/usePanelProcessing';
import { PRODUCT_TYPES, CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES, CLASSIC_PANEL_STATUS, COUNTRY_CODES } from '../../../constants/enums.js';
import campaignValues from '../../../utils/campaignValues';
import QueryStringService from '../../../services/queryStringService.js';
import styles from './AddressList.module.css';
import dateFunctions from '../../../utils/dateFunctions.js';
import numberFormatter from '../../../utils/numberFormatter.js';
import ImageModal from './ImageModal';

const AddressList = ({ orderData, orderlinesData, parameters, campaignType, campaignName, campaignPromiseType, selectedOrderlineMetaData }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [tableData, setTableData] = useState([]);
    const [modalIndex, setModalIndex] = useState(null);
    const queryStringService = useMemo(() => new QueryStringService(), []);
    const countryCode = queryStringService.getCurrentCountry();
    const filterFields = ["panelId", "environment", "address", "city", "design", "status"];
    const [showAll, setShowAll] = useState(false);
    const POSTED_LATE_DESCRIPTION = 'Posted late';
    const FAILED_DESCRIPTION = 'Failed';
    const ONGOING_DESCRIPTION = 'Ongoing';
    const extraText = useMemo(() => {
        return campaignValues.fetchEtxraTextForCampaignPromise(
            campaignPromiseType,
            CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES.ADDRESS_LIST_TAB
        );
    }, [campaignPromiseType]);

    const isNonMOMSFollowUp = useMemo(() =>
        campaignValues.isNonMOMSFollowUpCampaign(campaignPromiseType),
        [campaignPromiseType]
    );

    const { panels: originalPanels = [] } = usePanelProcessing(orderlinesData, campaignType);

    const hasImages = useMemo(() =>
        originalPanels.some(p => p.postingImageUrls?.length ||
            (p.panels && p.panels.some(panel => panel.postingImageUrls?.length))
        ), [originalPanels]);

    const imageList = useMemo(() => {
        return originalPanels?.flatMap(panel => {
            const entries = [];
            // Check for failed or early status before adding to images
            const isPanelFailed = panel.status?.toLowerCase() === CLASSIC_PANEL_STATUS.FAILED || 
                                panel.status?.toLowerCase() === CLASSIC_PANEL_STATUS.EARLY;
            
            if (!isPanelFailed && panel.postingImageUrls?.length) {
                panel.postingImageUrls
                    .filter(url => url?.trim())
                    .forEach(url => entries.push({ ...panel, imageUrl: url }));
            }

            if (panel.panels) {
                panel.panels.forEach(subPanel => {
                    // Filter out panels with failed or early status
                    const isSubPanelFailed = subPanel.status?.toLowerCase() === CLASSIC_PANEL_STATUS.FAILED || 
                                            subPanel.status?.toLowerCase() === CLASSIC_PANEL_STATUS.EARLY;
                    if (!isSubPanelFailed && subPanel.postingImageUrls?.length) {
                        subPanel.postingImageUrls
                            .filter(url => url?.trim())
                            .forEach(url => entries.push({ ...subPanel, imageUrl: url }));
                    }
                });
            }

            const uniqueImages = Array.from(new Map(entries.map(img => [img.imageUrl, img])).values());

            return uniqueImages;
        }) || [];
    }, [originalPanels]);

    useEffect(() => {
        setTableData(originalPanels);
    }, [originalPanels]);

    const filteredData = useMemo(() => {
        return tableData.filter(row =>
            filterFields.some(field =>
                row[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [tableData, searchTerm]);

    const getFormattedStatusText = useCallback((row) => {
        if (!row?.panels?.length) return "";

        const getStatusText = () => {
            if (row.panels.length === 1) {
                return [CLASSIC_PANEL_STATUS.LATE, CLASSIC_PANEL_STATUS.POSTED_LATE]
                    .includes(row.status.toLowerCase())
                    ? POSTED_LATE_DESCRIPTION
                    : row.status.toLowerCase() === CLASSIC_PANEL_STATUS.EARLY ? FAILED_DESCRIPTION : row.status;
            }

            const statuses = row.panels.map(panel => panel.status.toLowerCase());
            const uniqueStatuses = [...new Set(statuses)];

            if (uniqueStatuses.length === 1) {
                return [CLASSIC_PANEL_STATUS.LATE, CLASSIC_PANEL_STATUS.POSTED_LATE]
                    .includes(uniqueStatuses[0])
                    ? POSTED_LATE_DESCRIPTION
                    : row.panels[0].status.toLowerCase() === CLASSIC_PANEL_STATUS.EARLY ? FAILED_DESCRIPTION : row.panels[0].status;
            }

            if (uniqueStatuses.includes(CLASSIC_PANEL_STATUS.ONGOING)) {
                return ONGOING_DESCRIPTION;
            }

            const hasPosted = statuses.includes(CLASSIC_PANEL_STATUS.POSTED);
            const hasFailed = statuses.includes(CLASSIC_PANEL_STATUS.FAILED);
            const hasEarly = statuses.includes(CLASSIC_PANEL_STATUS.EARLY);
            const hasLate = statuses.includes(CLASSIC_PANEL_STATUS.LATE) ||
                statuses.includes(CLASSIC_PANEL_STATUS.POSTED_LATE);

            if ((hasLate || hasFailed || hasEarly) && !hasPosted) return CLASSIC_PANEL_STATUS.FAILED;
            if (hasPosted && (hasFailed || hasLate || hasEarly)) return CLASSIC_PANEL_STATUS.PARTIALLY;

            return CLASSIC_PANEL_STATUS.PARTIALLY;
        };

        const timestamp = row.timeStamp && !isNonMOMSFollowUp
            ? ` ${dateFunctions.getCountrySpecificDateTimeFormatFromDate(countryCode, moment(row.timeStamp))}`
            : '';

        return `${getStatusText()}${timestamp}`;
    }, [countryCode, isNonMOMSFollowUp]);    
    
    const openModal = useCallback((imageUrl) => {
        const index = imageList.findIndex(img => {
            // Only open modal for images that don't have failed or early status
            const status = img.status?.toLowerCase();
            const isFailed = status === CLASSIC_PANEL_STATUS.FAILED || 
                             status === CLASSIC_PANEL_STATUS.EARLY;
            
            return img.imageUrl === imageUrl && !isFailed;
        });
        
        if (index !== -1) setModalIndex(index);
    }, [imageList]);

    const closeModal = useCallback(() => setModalIndex(null), []);
    const showPrev = useCallback(() => setModalIndex(prev => (prev > 0 ? prev - 1 : imageList.length - 1)), [imageList.length]);
    const showNext = useCallback(() => setModalIndex(prev => (prev < imageList.length - 1 ? prev + 1 : 0)), [imageList.length]);

    // Flattens a grouped row into one detail row per (date, picture). A date with several
    // pictures produces one detail row per picture so every subrow shows a single image.
    const buildExpandedRows = useCallback((rowData) => {
        const datePanels = rowData.panels && rowData.panels.length > 1
            ? rowData.panels
            : [rowData];

        return datePanels.flatMap(panel => {
            const panelStatus = panel.status?.toLowerCase();
            const isFailed = panelStatus === CLASSIC_PANEL_STATUS.FAILED ||
                             panelStatus === CLASSIC_PANEL_STATUS.EARLY;
            const urls = isFailed ? [] : (panel.postingImageUrls || []).filter(url => url?.trim());

            if (urls.length === 0) {
                return [{ panel, imageUrl: null }];
            }

            return urls.map(url => ({ panel, imageUrl: url }));
        });
    }, []);

    const columns = useMemo(() => {
        const getPlayColumns = () => [
            { accessorKey: 'panelId', header: 'Panel Id', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'environment', header: 'Type', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'address', header: 'Address', cell: info => <div className={styles.address}>{info.getValue()}</div>, enableSorting: true },
            { accessorKey: 'city', header: 'City', cell: info => info.getValue(), enableSorting: true },
            ...(countryCode?.toLowerCase() === COUNTRY_CODES.SWEDEN ? [{
                accessorKey: 'soV', header: 'SOV', cell: info => numberFormatter.numberWithOneDecAndProcent(info.getValue()), enableSorting: true
            }] : []),
            ...(campaignValues.showAddressListVACDeliveredValue(campaignPromiseType) ? [{
                accessorKey: 'delivered', header: 'VAC', cell: info => numberFormatter.numberWithSpaces(info.getValue()), enableSorting: true
            }] : [])
        ];

        const getClassicColumns = () => [
            { accessorKey: 'panelId', header: 'Panel Id', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'environment', header: 'Type', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'address', header: 'Address', cell: info => <div className={styles.address}>{info.getValue()}</div>, enableSorting: true },
            { accessorKey: 'city', header: 'City', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'design', header: 'Design', cell: info => <span className={styles.design}>{info.getValue()}</span>, enableSorting: true, meta: { className: styles.designCol } },
            { accessorKey: 'plannedDate', header: 'Planned Posting Date', cell: info => <span className={styles.design}>{dateFunctions.getCountrySpecificDateFormatFromDate(countryCode, info.getValue())}</span>, enableSorting: true, meta: { className: styles.designCol } },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => {
                    const isExpandable = buildExpandedRows(row.original).length > 1;
                    return (
                        <span
                            className={`${styles.status} ${isExpandable ? styles.clickableStatus : ''}`}
                            onClick={isExpandable ? () => row.toggleExpanded() : undefined}
                            style={{ cursor: isExpandable ? 'pointer' : 'default' }}
                        >
                            {isExpandable ? (row.getIsExpanded() ? '(-)' : '(+)') : ''} {getFormattedStatusText(row.original)}
                        </span>
                    );
                },
                enableSorting: true,
                meta: { className: styles.statusCol }
            },
            {
                accessorKey: 'postingImageUrls',
                header: 'Picture',
                cell: ({ row }) => {
                    // Check if status is failed or early
                    const status = row.original.status?.toLowerCase();
                    const isFailed = status === CLASSIC_PANEL_STATUS.FAILED || 
                                     status === CLASSIC_PANEL_STATUS.EARLY;
                    
                    // Don't show image for failed postings
                    if (isFailed) {
                        return <span></span>;
                    }

                    const urls = (row.original.postingImageUrls || []).filter(url => url?.trim());
                    // A single image is shown inline. Multiple images are revealed through the
                    // expandable subrows (indicated by the +/- toggle in the Status column).
                    if (urls.length !== 1) {
                        return <span></span>;
                    }

                    return (
                        <img
                            src={urls[0]}
                            alt="Thumbnail"
                            className={styles.thumbnailImage}
                            onClick={() => openModal(urls[0])}
                        />
                    );
                },
                enableSorting: false,
                meta: { className: styles.pictureCol }
            }
        ];

        return campaignType === PRODUCT_TYPES.PLAY ? getPlayColumns() : getClassicColumns();
    }, [campaignPromiseType, campaignType, buildExpandedRows]);

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: {
            pagination: {
                pageSize: 50
            }
        },
        manualPagination: showAll
    });


    return (
        <div className={styles.addressContainer}>
            <div className={styles.stickyHeader}>
                {extraText}
                <input
                    type="text"
                    className={styles.searchBar}
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    const sortOrder = header.column.getIsSorted();
                                    return (
                                        <th key={header.id} onClick={header.column.getToggleSortingHandler()} className={styles.sortableHeader}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <span className={styles.sortArrow}>
                                                {sortOrder === 'asc' ? ' ↑' : sortOrder === 'desc' ? ' ↓' : ' ↑↓'}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {(showAll ? table.getPrePaginationRowModel().rows : table.getRowModel().rows).map(row => (
                            <React.Fragment key={row.id}>
                                <tr>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} className={cell.column.columnDef.meta?.className || ''}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                                {row.getIsExpanded() && (
                                    buildExpandedRows(row.original).map(({ panel, imageUrl }, index) => (
                                        <tr key={`${row.id}-expanded-${index}`} className={styles.expandedRow}>
                                            <td colSpan={columns.findIndex(item => item.accessorKey === 'design')}></td>
                                            <td>
                                                <div className={`${styles.design} ${styles.expandedContent}`}>
                                                    {panel.design}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={`${styles.design} ${styles.expandedContent}`}>
                                                    {dateFunctions.getCountrySpecificDateFormatFromDate(countryCode, moment(panel.plannedDate))}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={`${styles.status} ${styles.expandedContent}`}>
                                                    {panel.status.toLowerCase() === CLASSIC_PANEL_STATUS.LATE ? POSTED_LATE_DESCRIPTION :
                                                        panel.status.toLowerCase() === CLASSIC_PANEL_STATUS.EARLY ? FAILED_DESCRIPTION : panel.status}
                                                    {!isNonMOMSFollowUp && (
                                                        <>
                                                            {' '}
                                                            {dateFunctions.getCountrySpecificDateTimeFormatFromDate(countryCode, moment(panel.timeStamp))}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            {hasImages && (
                                                <td>
                                                    {imageUrl ? (
                                                        <img
                                                            src={imageUrl}
                                                            alt="Thumbnail"
                                                            className={styles.thumbnailImage}
                                                            onClick={() => openModal(imageUrl)}
                                                        />
                                                    ) : (
                                                        <span></span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={styles.paginationContainer}>
                {!showAll ? (
                    <>
                        <div className={styles.paginationCenter}>
                        <button className={styles.paginationButton} onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                                &lt;
                        </button>
                        <span> Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} </span>
                        <button className={styles.paginationButton} onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                            &gt;
                            </button>
                        </div>
                    </>
                ) : null}
                <button
                    className={`${styles.paginationButton} ${styles.triggerPagination}`}
                    onClick={() => setShowAll(!showAll)}
                >
                    {showAll ? "Back to Paginated View" : "Show All"}
                </button>
            </div>
            <ImageModal
                countryCode={countryCode}
                images={imageList}
                currentIndex={modalIndex}
                onClose={closeModal}
                onPrev={showPrev}
                onNext={showNext}
                hidePostingDate={isNonMOMSFollowUp}
            />
        </div>
    );
};

export default React.memo(AddressList);
